import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useModal } from 'react-modal-hook';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeactivateParticipantMutation from './DeactivateParticipantMutation';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';

interface Participant {
  id: string;
  email: string;
  full_name: string | null;
}

interface UseDeactivateParticipantModalProps {
  participant: Participant;
  refetchQueries?: DocumentNode[];
}

const useDeactivateParticipantModal = ({
  participant,
  refetchQueries,
}: UseDeactivateParticipantModalProps,
) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [deactivateParticipant, { loading: deactivating }] = useMutation(
    DeactivateParticipantMutation,
    {
      variables: {
        input: {
          id: participant?.id,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        closeModal();

        notifier.success(t('notifications.contact_deleted'));
      },
    },
  );

  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal>
        <UI.H4 sc={{ mb: 2 }}>
          {t('delete_contact')}
        </UI.H4>
        <UI.Paragraph>
          <Trans
            i18nKey="delete_contact_confirmation"
            values={{ name: participant.full_name }}
          >
            <UI.Strong />
          </Trans>
        </UI.Paragraph>
        <UI.Paragraph>
          {t('delete_contact_description')}
        </UI.Paragraph>
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button onClick={() => closeModal()} disabled={deactivating}>
            {t('cancel')}
          </UI.Button>
          {' '}
          <UI.ConfirmButton
            onConfirm={deactivateParticipant}
            loading={deactivating}
            render={(start, disabled) => (
              <UI.Button
                onClick={() => start()}
                sc={{ brand: 'error' }}
                disabled={disabled}
              >
                {t('delete')}
              </UI.Button>
            )}
          />
        </UI.Div>
      </UI.Modal>
    ),
    [participant, deactivating],
  );

  return [openModal, closeModal];
};

export default useDeactivateParticipantModal;
